import { PercentPipe } from '@angular/common';
import { PrepackQuantityPipe } from '@epicuro-next/ataraxia/prepack-quantity-pipe';
import { SizeGridRowConfig } from '@epicuro-next/data/order-book';
import {
  Delivery,
  DeliveryInfo,
  IOrdersSettings,
  Material,
  OrderItem,
  OrderStatus,
} from '@epicuro-next/models/order-book';
import {
  hashObject,
  isNonNullish,
  uniqueByPropertyPath,
} from '@epicuro-next/utilities';

import {
  DetailsPageActions,
  IFormatMap,
  IOrderPipesMap,
  OrderMaterialsAdditionalPropertiesKeys,
} from '../../models';
import { OrderItemLine } from '../../models/order-item-line.model';
import { IOrderRowProduct } from '../../models/order-product.model';
import {
  getDateValue,
  getGenderValue,
  getNumberValue,
  getOrderItemQuantity,
  getOrderThumbnails,
  getPercentValue,
} from '../get-value/get-value';
import { getColumns, getRows } from '../map-sizes-to-grid/map-sizes-to-grid';

const statusValues = Object.keys(OrderStatus);

export function mapOrderItem(
  orderItem: OrderItem & {
    variants?: OrderItem[];
  },
  locale: string,
  settings: IOrdersSettings,
  pipes: IOrderPipesMap,
  formatMap: IFormatMap,
  products: IOrderRowProduct[],
  orderDetailDeliveries: Delivery[],
  showStyleInfoInSizeGrid: boolean,
  selectedScaleSet?: string,
  customRowsConfig: SizeGridRowConfig[] = [],
  rowTitleProperty: keyof Material = 'title',
): OrderItemLine {
  const itemProducts = orderItem.materials
    .map(({ productId }) => products.find(({ id }) => productId === id))
    .filter(isNonNullish);

  const firstProduct = itemProducts[0];

  const columns = getColumns(firstProduct, selectedScaleSet);
  const rows = getRows(
    orderItem.materials,
    itemProducts,
    showStyleInfoInSizeGrid,
    selectedScaleSet,
    {
      netPriceUnit: orderItem.netPrice.unit,
    },
    customRowsConfig,
    rowTitleProperty,
  );

  const mappedOrderItem = {
    ...orderItem,
    grid: {
      columns,
      rows,
    },
    internalId: hashObject(orderItem) as string,
    markup: getNumberValue<number>(orderItem.markup, pipes.number),
    requestedDate: getDateValue(orderItem.requestedDate, locale),
    confirmedDate: getDateValue(orderItem.confirmedDate, locale),
    retailPrice: {
      ...orderItem.retailPrice,
      unit: getNumberValue<number>(orderItem.retailPrice.unit, pipes.number),
    },
    grossPrice: {
      ...orderItem.grossPrice,
      unit: getNumberValue<number>(orderItem.grossPrice.unit, pipes.number),
    },
    netPrice: {
      ...orderItem.netPrice,
      unit: getNumberValue<number>(orderItem.netPrice.unit, pipes.number),
      total: getNumberValue<number>(orderItem.netPrice.total, pipes.number),
    },
    estimatedTimeToArrive: getDateValue(
      orderItem.estimatedTimeToArrive,
      locale,
    ),
    discount: {
      ...orderItem.discount,
      percentage: getPercentValue(
        orderItem.discount.percentage,
        pipes.percent as PercentPipe,
        formatMap.discountFormat as string,
      ) as number,
    },
    invoicesEnabled:
      Object.values(orderItem.invoices ?? {}).filter(Boolean).length > 0,
    requestedQuantity: getOrderItemQuantity(
      orderItem,
      'requestedQuantity',
      pipes.prepackQuantity as PrepackQuantityPipe,
    ) as number,
    deliveredQuantity: getOrderItemDeliveryQuantity(orderItem),
    [OrderMaterialsAdditionalPropertiesKeys.InvoicesAction]: {
      type: DetailsPageActions.InvoicesModal,
      value: orderItem.invoices ?? {},
    },
    [OrderMaterialsAdditionalPropertiesKeys.ImageUrl]:
      firstProduct?.thumbnail ??
      getOrderThumbnails(
        {
          productId: orderItem.materials[0].productId,
          brandId: orderItem.brandId,
        },
        settings,
      ),
    [OrderMaterialsAdditionalPropertiesKeys.CSPath]: [
      'cs',
      orderItem.brandId,
      orderItem.rootId ?? orderItem.materials[0].productId,
      orderItem.materials[0].productId,
    ],
    deliveryInfo: uniqueByPropertyPath(
      orderItem.deliveryInfo,
      'deliveryNumber',
      'deliveryDate',
      'trackingNumber',
    ).map((deliveryInfo) => {
      return {
        ...deliveryInfo,
        shippingDate: getDateValue(deliveryInfo.shippingDate, locale),
        trackingUrl:
          deliveryInfo.trackingUrl ??
          findTrackingUrl(orderDetailDeliveries, deliveryInfo),
        deliveryNoteUrl: findDeliveryNoteUrl(
          orderDetailDeliveries,
          deliveryInfo,
        ),
      };
    }),
    variants: (orderItem.variants ?? []).map((variant) =>
      mapOrderItem(
        variant,
        locale,
        settings,
        pipes,
        formatMap,
        products,
        orderDetailDeliveries,
        showStyleInfoInSizeGrid,
        selectedScaleSet,
        customRowsConfig,
        rowTitleProperty,
      ),
    ),
    statuses: (
      orderItem.variants ?? [
        {
          salesOrderLineStatus: orderItem.salesOrderLineStatus,
        },
      ]
    )
      .map(({ salesOrderLineStatus }) => salesOrderLineStatus)
      .sort(
        (a, b) => statusValues.indexOf(a ?? '') - statusValues.indexOf(b ?? ''),
      ),
    [OrderMaterialsAdditionalPropertiesKeys.GenderConfig]: getGenderValue(
      orderItem.materials[0].gender,
    ),
  };

  return mappedOrderItem;
}

function findTrackingUrl(
  deliveries: Delivery[],
  deliveryInfo: DeliveryInfo,
): string {
  return (
    deliveries
      .flatMap(({ tracking }) => tracking)
      .find(
        (trackInfo) => trackInfo.trackingNumber === deliveryInfo.trackingNumber,
      )?.trackingUrl ?? ''
  );
}

function findDeliveryNoteUrl(
  deliveries: Delivery[],
  deliveryInfo: DeliveryInfo,
): string {
  return (
    deliveries.find(
      (delivery) => delivery.number === deliveryInfo.deliveryNumber,
    )?.noteUrl ?? ''
  );
}

export function getOrderItemDeliveryQuantity({
  deliveryInfo,
}: OrderItem): number {
  return deliveryInfo.reduce(
    (acc, { deliveredQuantity }) => acc + parseInt(deliveredQuantity ?? 0, 10),
    0,
  );
}
